/**
 * gameTickerParts.jsx — presentational sub-components for GameTicker.
 * Extracted verbatim from components/GameTicker.jsx (god-file decomposition).
 * TickerChrome, SkeletonTickerItem, and TickerItem are exported for use by the
 * main GameTicker component; SkeletonShimmer, TeamLogo, and
 * LeagueSectionDivider are internal helpers used within this file.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Platform, Animated, Image, TouchableOpacity, Easing } from 'react-native';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import { getTeamLogo } from '../data/logos';
import { styles } from './gameTickerStyles';

export const TickerChrome = ({ children }) =>
  Platform.OS === 'web' ? (
    <View style={[styles.blurContainer, styles.blurContainerWeb]}>{children}</View>
  ) : (
    <BlurView intensity={40} tint="dark" style={styles.blurContainer}>
      {children}
    </BlurView>
  );

// Skeleton shimmer animation component
const SkeletonShimmer = ({ width, height, borderRadius = 4, style }) => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    const shimmer = Animated.loop(
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1500,
        easing: Easing.linear,
        useNativeDriver: false,
      })
    );
    shimmer.start();
    return () => shimmer.stop();
  }, []);
  
  const translateX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-width, width],
  });
  
  return (
    <View style={[{ 
      width, 
      height, 
      borderRadius,
      backgroundColor: 'rgba(255,255,255,0.08)',
      overflow: 'hidden',
    }, style]}>
      <Animated.View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        transform: [{ translateX }],
      }}>
        <View style={{
          width: width * 0.5,
          height: '100%',
          backgroundColor: 'rgba(255,255,255,0.08)',
        }} />
      </Animated.View>
    </View>
  );
};

// Single skeleton ticker item
export const SkeletonTickerItem = ({ index }) => {
  return (
    <View style={styles.tickerItem}>
      {/* Status badge skeleton */}
      <SkeletonShimmer width={50} height={18} borderRadius={4} />
      
      {/* League skeleton */}
      <SkeletonShimmer width={30} height={12} borderRadius={3} />
      
      {/* Teams skeleton */}
      <View style={styles.teamsContainer}>
        <SkeletonShimmer width={18} height={18} borderRadius={9} />
        <SkeletonShimmer width={60} height={14} borderRadius={3} />
        <View style={{ width: 12 }} />
        <SkeletonShimmer width={60} height={14} borderRadius={3} />
        <SkeletonShimmer width={18} height={18} borderRadius={9} />
      </View>
      
      {/* Time skeleton */}
      <SkeletonShimmer width={70} height={12} borderRadius={3} />
      
      {/* Divider */}
      <View style={styles.divider} />
    </View>
  );
};

// Team logo placeholder - shows team initial
const TeamLogo = ({ logoUrl, teamName, size = 20 }) => {
  const [hasError, setHasError] = useState(false);
  const initial = teamName ? teamName.charAt(0).toUpperCase() : '?';
  
  if (logoUrl && !hasError) {
    return (
      <Image 
        source={{ uri: logoUrl }} 
        style={{ width: size, height: size, borderRadius: size / 2 }}
        resizeMode="contain"
        onError={() => setHasError(true)}
      />
    );
  }
  
  // Fallback to initial
  return (
    <View style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor: 'rgba(255,255,255,0.15)',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Text style={{
        fontSize: size * 0.5,
        fontWeight: '700',
        color: 'rgba(255,255,255,0.7)',
        fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui" : 'System',
      }}>
        {initial}
      </Text>
    </View>
  );
};

// Helper to format team/player names based on user preference
const formatName = (fullName, format, isTeam = true) => {
  if (!fullName || fullName === 'TBD') return fullName;
  
  const name = fullName.trim();
  
  switch (format) {
    case 'full':
      // Return full name as-is
      return name;
      
    case 'abbreviated':
      if (isTeam) {
        // For teams: Try to get standard abbreviation or create one
        // Common team abbreviations
        const teamAbbreviations = {
          'cleveland cavaliers': 'CLE', 'golden state warriors': 'GSW', 'los angeles lakers': 'LAL',
          'los angeles clippers': 'LAC', 'boston celtics': 'BOS', 'miami heat': 'MIA',
          'milwaukee bucks': 'MIL', 'phoenix suns': 'PHX', 'denver nuggets': 'DEN',
          'minnesota timberwolves': 'MIN', 'dallas mavericks': 'DAL', 'oklahoma city thunder': 'OKC',
          'new york knicks': 'NYK', 'brooklyn nets': 'BKN', 'philadelphia 76ers': 'PHI',
          'chicago bulls': 'CHI', 'toronto raptors': 'TOR', 'indiana pacers': 'IND',
          'detroit pistons': 'DET', 'atlanta hawks': 'ATL', 'charlotte hornets': 'CHA',
          'washington wizards': 'WAS', 'orlando magic': 'ORL', 'memphis grizzlies': 'MEM',
          'new orleans pelicans': 'NOP', 'houston rockets': 'HOU', 'san antonio spurs': 'SAS',
          'utah jazz': 'UTA', 'sacramento kings': 'SAC', 'portland trail blazers': 'POR',
          // NFL
          'kansas city chiefs': 'KC', 'buffalo bills': 'BUF', 'san francisco 49ers': 'SF',
          'dallas cowboys': 'DAL', 'philadelphia eagles': 'PHI', 'green bay packers': 'GB',
          'new england patriots': 'NE', 'baltimore ravens': 'BAL', 'cincinnati bengals': 'CIN',
          'pittsburgh steelers': 'PIT', 'los angeles rams': 'LAR', 'los angeles chargers': 'LAC',
          'seattle seahawks': 'SEA', 'new york giants': 'NYG', 'new york jets': 'NYJ',
          'denver broncos': 'DEN', 'las vegas raiders': 'LV', 'arizona cardinals': 'ARI',
          'detroit lions': 'DET', 'chicago bears': 'CHI', 'minnesota vikings': 'MIN',
          'tampa bay buccaneers': 'TB', 'miami dolphins': 'MIA', 'cleveland browns': 'CLE',
          // MLB
          'new york yankees': 'NYY', 'new york mets': 'NYM', 'los angeles dodgers': 'LAD',
          'boston red sox': 'BOS', 'chicago cubs': 'CHC', 'chicago white sox': 'CWS',
          // NHL
          'vegas golden knights': 'VGK', 'colorado avalanche': 'COL', 'tampa bay lightning': 'TBL',
        };
        const lowerName = name.toLowerCase();
        if (teamAbbreviations[lowerName]) return teamAbbreviations[lowerName];
        // Fallback: take first 3 letters of last word
        const words = name.split(' ');
        return words[words.length - 1].substring(0, 3).toUpperCase();
      } else {
        // For players: "Carlos Alcaraz" -> "C. Alcaraz"
        const parts = name.split(' ');
        if (parts.length >= 2) {
          return `${parts[0].charAt(0)}. ${parts.slice(1).join(' ')}`;
        }
        return name;
      }
      
    case 'short':
    default:
      if (isTeam) {
        // For teams: Remove city name, keep team name
        // "Cleveland Cavaliers" -> "Cavaliers", "Los Angeles Lakers" -> "Lakers"
        const words = name.split(' ');
        if (words.length >= 2) {
          // Common two-word team names to keep together
          const twoWordTeams = ['trail blazers', 'red sox', 'white sox', 'blue jays', 'golden knights', '76ers'];
          const lastTwo = words.slice(-2).join(' ').toLowerCase();
          if (twoWordTeams.includes(lastTwo)) {
            return words.slice(-2).join(' ');
          }
          return words[words.length - 1];
        }
        return name;
      } else {
        // For players: Just last name "Carlos Alcaraz" -> "Alcaraz"
        const parts = name.split(' ');
        if (parts.length >= 2) {
          return parts[parts.length - 1];
        }
        return name;
      }
  }
};

// Helper to determine if name is likely a person (tennis, boxing, mma) vs team
const isPlayerSport = (league) => {
  const l = (league || '').toLowerCase();
  return l.includes('wta') || l.includes('atp') || l.includes('tennis') || 
         l.includes('ufc') || l.includes('mma') || l.includes('bellator') || l.includes('pfl') ||
         l.includes('boxing') || l.includes('golf') || l.includes('pga');
};

// Helper to parse various date formats
export const parseDate = (startDate) => {
  if (!startDate) return null;
  
  try {
    // Handle Firestore timestamp format { _seconds, _nanoseconds }
    if (startDate._seconds) {
      return new Date(startDate._seconds * 1000);
    }
    // Handle Firestore timestamp format { seconds, nanoseconds }
    if (startDate.seconds) {
      return new Date(startDate.seconds * 1000);
    }
    // Handle ISO string or Date object
    return new Date(startDate);
  } catch (e) {
    console.warn('[GameTicker] Failed to parse date:', startDate);
    return null;
  }
};

// Helper function to format period/quarter/inning
const formatPeriod = (period, league) => {
  const p = String(period).toLowerCase();
  const l = (league || '').toLowerCase();
  
  // Handle numeric periods
  const num = parseInt(p, 10);
  if (!isNaN(num)) {
    // Baseball uses "innings"
    if (l.includes('mlb') || l.includes('baseball')) {
      const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
      return `${num}${suffix}`;
    }
    // Hockey uses "periods"
    if (l.includes('nhl') || l.includes('hockey')) {
      const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
      return `${num}${suffix}`;
    }
    // Basketball/Football uses "quarters"
    const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
    return `${num}${suffix}`;
  }
  
  // Handle text periods
  if (p.includes('half')) return p.includes('1') ? '1st Half' : '2nd Half';
  if (p.includes('ot') || p.includes('overtime')) return 'OT';
  if (p.includes('final')) return 'Final';
  
  return period;
};

// Helper function to determine game status.
// ⭐ In freeze/archive mode, pass referenceTime (ISO string or ms) so cached
// fixtures aren't marked as "ended" relative to the real wall clock.
export const getGameStatus = (startDate, referenceTime) => {
  const gameTime = parseDate(startDate);
  if (!gameTime) return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
  
  try {
    const now = referenceTime ? new Date(referenceTime) : new Date();
    const diffMs = gameTime - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    // Game started more than 3 hours ago = ended
    if (diffHours < -3) {
      return { status: 'ended', label: 'FINAL', color: '#6B7280', icon: 'checkmark-circle-outline' };
    }
    // Game is live or recently started (within last 3 hours)
    else if (diffHours < 0) {
      return { status: 'live', label: 'LIVE', color: '#10B981', icon: 'radio-button-on', pulse: true };
    }
    // Game starts within next hour = starting soon
    else if (diffHours < 1) {
      return { status: 'soon', label: 'SOON', color: '#F59E0B', icon: 'alarm-outline' };
    }
    // Upcoming
    else {
      return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
    }
  } catch (e) {
    return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
  }
};

// Format game time
const formatGameTime = (startDate) => {
  const gameTime = parseDate(startDate);
  if (!gameTime) return '';
  
  try {
    const now = new Date();
    const isToday = gameTime.toDateString() === now.toDateString();
    const isTomorrow = gameTime.toDateString() === new Date(now.getTime() + 86400000).toDateString();
    
    const timeStr = gameTime.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;
    
    return gameTime.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return '';
  }
};

// ⭐ League Section Divider - Bold visual separator between leagues
const LeagueSectionDivider = ({ league, isStart = true }) => {
  return (
    <View style={styles.leagueDividerContainer}>
      {/* Gradient bar */}
      <View style={styles.leagueDividerBar} />
      {/* League badge */}
      <View style={styles.leagueDividerBadge}>
        <Text style={styles.leagueDividerText}>{league}</Text>
      </View>
      {/* Gradient bar */}
      <View style={styles.leagueDividerBar} />
    </View>
  );
};

// Single game item in the ticker
export const TickerItem = ({ fixture, onPress, onSGPPress, onLivePress, index, nameFormat, hasCachedSGPs = false, showSGPIndicator = false, liveScore = null, isFirstInLeague = false, isLastInLeague = false, onRowLayout, referenceTime = null }) => {
  const status = getGameStatus(fixture.start_date, referenceTime);
  const pulseAnim = useRef(new Animated.Value(1)).current;
  
  // Pulse animation for live games
  useEffect(() => {
    if (status.pulse) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 0.5,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    }
  }, [status.pulse]);

  const homeTeamFull = fixture.home_team || 'TBD';
  const awayTeamFull = fixture.away_team || 'TBD';
  const league = (fixture.league || '').toLowerCase();
  
  // Better sport detection from league
  const detectSport = (leagueStr) => {
    const l = leagueStr.toLowerCase();
    if (l.includes('nba') || l.includes('ncaab') || l.includes('wnba')) return 'basketball';
    if (l.includes('nfl') || l.includes('ncaaf') || l.includes('cfl')) return 'football';
    if (l.includes('mlb')) return 'baseball';
    if (l.includes('nhl')) return 'hockey';
    if (l.includes('mls') || l.includes('epl') || l.includes('premier') || l.includes('la liga') || l.includes('bundesliga') || l.includes('serie a') || l.includes('champions') || l.includes('uefa')) return 'soccer';
    return fixture.sport || 'basketball';
  };
  
  const sport = detectSport(league);
  const isPlayer = isPlayerSport(league);
  
  // Format names based on user preference
  const homeTeam = formatName(homeTeamFull, nameFormat, !isPlayer);
  const awayTeam = formatName(awayTeamFull, nameFormat, !isPlayer);
  
  // Get logos - try fixture data first, then fall back to getTeamLogo lookup
  const homeLogo = fixture.home_logo || fixture.home_team_logo || fixture.home_image || getTeamLogo(homeTeamFull, sport);
  const awayLogo = fixture.away_logo || fixture.away_team_logo || fixture.away_image || getTeamLogo(awayTeamFull, sport);
  
  return (
    <View
      style={{ flexDirection: 'row', alignItems: 'center' }}
      onLayout={onRowLayout}
      collapsable={false}
    >
      {/* ⭐ League Section Start Divider */}
      {isFirstInLeague && (
        <LeagueSectionDivider league={league.toUpperCase()} isStart={true} />
      )}
      
      <TouchableOpacity 
        onPress={onPress}
        activeOpacity={0.7}
        style={styles.tickerItem}
      >
        {/* Status Badge — LIVE is a clickable button, others are static */}
        {status.status === 'live' ? (
          <TouchableOpacity
            style={[styles.statusBadge, { backgroundColor: `${status.color}20`, borderWidth: 1, borderColor: `${status.color}40` }, Platform.OS === 'web' && { cursor: 'pointer' }]}
            onPress={(e) => { e.stopPropagation(); if (onLivePress) onLivePress(fixture); }}
            activeOpacity={0.7}
          >
            <Animated.View style={[styles.liveBadgeDot, { opacity: pulseAnim }]} />
            <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
          </TouchableOpacity>
        ) : (
          <View style={[styles.statusBadge, { backgroundColor: `${status.color}20` }]}>
            <Animated.View style={status.pulse ? { opacity: pulseAnim } : null}>
              <Ionicons name={status.icon} size={10} color={status.color} />
            </Animated.View>
            <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
          </View>
        )}
      
      {/* League Tag */}
      {league && (
        <Text style={styles.leagueTag}>{league.toUpperCase()}</Text>
      )}
      
      {/* Teams with Logos and Live Score */}
      <View style={styles.teamsContainer}>
        <TeamLogo logoUrl={awayLogo} teamName={awayTeamFull} size={18} />
        <Text style={styles.teamText} numberOfLines={1}>{awayTeam}</Text>
        
        {/* Live Score Display - Glassmorphic */}
        {liveScore && liveScore.is_live && liveScore.away_score !== null ? (
          <View style={styles.liveScoreContainer}>
            <View style={styles.liveScoreInner}>
              <Text style={styles.liveScoreText}>{liveScore.away_score}</Text>
              <Text style={styles.liveScoreDash}>-</Text>
              <Text style={styles.liveScoreText}>{liveScore.home_score}</Text>
            </View>
            {liveScore.period && (
              <Text style={styles.livePeriodText}>
                {formatPeriod(liveScore.period, league)}
              </Text>
            )}
          </View>
        ) : (
          <Text style={styles.vsText}>@</Text>
        )}
        
        <Text style={styles.teamText} numberOfLines={1}>{homeTeam}</Text>
        <TeamLogo logoUrl={homeLogo} teamName={homeTeam} size={18} />
      </View>
      
      {/* Time (hide if showing live score with period) */}
      {(!liveScore || !liveScore.is_live) && (
        <Text style={styles.timeText}>{formatGameTime(fixture.start_date)}</Text>
      )}
      
      {/* ⭐ SGP Cache Indicator - clickable to view SGPs */}
      {showSGPIndicator && hasCachedSGPs && (
        <TouchableOpacity 
          style={styles.sgpIndicator}
          onPress={(e) => {
            e.stopPropagation();
            if (onSGPPress) onSGPPress(fixture);
          }}
          activeOpacity={0.7}
        >
          <View style={styles.sgpDot} />
          <Text style={styles.sgpIndicatorText}>SGP</Text>
        </TouchableOpacity>
      )}

      {/* LIVE badge moved inline to status badge slot above */}
      
      {/* Divider - bolder if last in league */}
      <View style={[styles.divider, isLastInLeague && styles.leagueEndDivider]} />
    </TouchableOpacity>
    </View>
  );
};
